import { MAX_FILE_SIZE, MAX_FILES } from "@elaso/shared/constants/upload";
import { Upload } from "lucide-react";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from "react";
import { useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";

type ChatDropzoneContextType = {
	setOnFilesDropped: (handler: (files: File[]) => void) => void;
};

const ChatDropzoneContext = createContext<ChatDropzoneContextType | undefined>(
	undefined
);

export function useChatDropzone() {
	return useContext(ChatDropzoneContext);
}

type ChatDropzoneProps = {
	children: ReactNode;
	className?: string;
	disabled?: boolean;
};

export function ChatDropzone({
	children,
	className,
	disabled = false,
}: ChatDropzoneProps) {
	const onFilesDroppedRef = useRef<(files: File[]) => void>(
		(_files: File[]) => {}
	);

	const setOnFilesDropped = useCallback((handler: (files: File[]) => void) => {
		onFilesDroppedRef.current = handler;
	}, []);

	const { getRootProps, isDragActive } = useDropzone({
		onDrop: (acceptedFiles) => {
			if (acceptedFiles.length > 0 && !disabled) {
				const validFiles = acceptedFiles
					.filter((file) => file.size <= MAX_FILE_SIZE)
					.slice(0, MAX_FILES);
				if (validFiles.length > 0) {
					onFilesDroppedRef.current(validFiles);
				}
			}
		},
		noClick: true,
		noKeyboard: true,
		disabled,
	});

	const contextValue = useMemo(
		() => ({ setOnFilesDropped }),
		[setOnFilesDropped]
	);

	return (
		<ChatDropzoneContext.Provider value={contextValue}>
			<div {...getRootProps()} className={cn("relative h-full", className)}>
				{children}
				{isDragActive && !disabled && (
					<div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
						<div className="flex flex-col items-center gap-3 rounded-xl border-2 border-primary border-dashed bg-primary/5 px-12 py-8">
							<div className="rounded-full bg-primary/10 p-4">
								<Upload className="size-8 text-primary" />
							</div>
							<div className="text-center">
								<p className="font-semibold text-lg">Drop files here</p>
								<p className="text-muted-foreground text-sm">
									Up to {MAX_FILES} files, max {MAX_FILE_SIZE} each
								</p>
							</div>
						</div>
					</div>
				)}
			</div>
		</ChatDropzoneContext.Provider>
	);
}
